import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listAdminAccounts } from "@/lib/admin-overview.functions";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/accounts/")({
  component: AdminAccountsPage,
});

function AdminAccountsPage() {
  const fetch = useServerFn(listAdminAccounts);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-accounts", search, plan, status],
    queryFn: () =>
      fetch({
        data: {
          search: search || null,
          plan: plan === "all" ? null : plan,
          status: status === "all" ? null : status,
          limit: 100,
        },
      }),
  });

  const accounts = data?.accounts ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Search</label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Account name…"
            className="w-64"
          />
        </div>
        <Picker
          label="Plan"
          value={plan}
          onChange={setPlan}
          options={["all", "trial", "starter", "pro", "scale", "enterprise"]}
        />
        <Picker
          label="Status"
          value={status}
          onChange={setStatus}
          options={["all", "active", "past_due", "cancelled", "paused"]}
        />
        <p className="ml-auto self-end text-xs text-muted-foreground">
          {accounts.length} account{accounts.length === 1 ? "" : "s"}
        </p>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && accounts.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No accounts match these filters.
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <Th>Name</Th>
              <Th>Plan</Th>
              <Th>Status</Th>
              <Th className="text-right">Workspaces</Th>
              <Th className="text-right">Members</Th>
              <Th className="text-right">AI calls (30d)</Th>
              <Th className="text-right">AI cost (30d)</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {accounts.map((a: any) => (
              <tr key={a.id} className="hover:bg-muted/20">
                <Td>
                  <Link
                    to="/admin/accounts/$accountId"
                    params={{ accountId: a.id }}
                    className="font-medium text-primary hover:underline"
                  >
                    {a.name}
                  </Link>
                  {a.is_admin_comped && (
                    <Badge variant="outline" className="mt-1 text-[10px]">
                      comped
                    </Badge>
                  )}
                </Td>
                <Td>
                  <Badge variant="secondary">{a.plan}</Badge>
                </Td>
                <Td>
                  <Badge
                    variant={a.plan_status === "active" ? "default" : "outline"}
                  >
                    {a.plan_status}
                  </Badge>
                </Td>
                <Td className="text-right tabular-nums">
                  {a.workspace_count}
                  <span className="text-muted-foreground"> / {a.workspace_limit}</span>
                </Td>
                <Td className="text-right tabular-nums">
                  {a.member_count}
                  <span className="text-muted-foreground"> / {a.team_member_limit}</span>
                </Td>
                <Td className="text-right tabular-nums">
                  {a.ai_calls_30d.toLocaleString()}
                </Td>
                <Td className="text-right tabular-nums">
                  ${a.ai_cost_30d_usd.toFixed(2)}
                </Td>
                <Td className="text-muted-foreground">
                  {a.created_at
                    ? new Date(a.created_at).toLocaleDateString()
                    : ","}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Picker({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o === "all" ? "All" : o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
